import { useRef, useCallback, RefObject } from 'react';

/**
 * Handles scroll-driven header collapse animation with snap-to-end behaviour.
 *
 * When the user stops scrolling mid-animation (progress between 0 and 1) the
 * hook polls every frame until the scroll container has fully settled, then
 * smoothly snaps to either the fully-collapsed or fully-expanded position.
 *
 * @param containerRef  Ref to the scrollable DOM element.
 * @param collapseDistance  Scroll distance (px) over which the animation runs.
 * @param applyProgress  Callback that applies CSS updates for a given progress
 *                       value (0 = expanded, 1 = collapsed).  The second
 *                       argument signals whether a CSS transition should be
 *                       used (true when snapping, false during live scroll).
 * @returns  handleScroll — call this from the container's onScroll handler,
 *           passing the current scrollTop value.
 */
export function useScrollSnap(
  containerRef: RefObject<HTMLElement | null>,
  collapseDistance: number,
  applyProgress: (progress: number, smooth: boolean) => void,
): (scrollTop: number) => void {
  const scrollSnapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScrollTop = useRef(0);

  const handleScroll = useCallback(
    (scrollTop: number) => {
      const progress = Math.min(scrollTop / collapseDistance, 1);
      applyProgress(progress, false);

      lastScrollTop.current = scrollTop;
      if (scrollSnapTimer.current) clearTimeout(scrollSnapTimer.current);

      if (progress > 0 && progress < 1) {
        const checkStopped = () => {
          const cur = containerRef.current?.scrollTop ?? 0;
          const moved = Math.abs(cur - lastScrollTop.current);
          lastScrollTop.current = cur;

          if (moved < 1) {
            // Scroll has fully settled — snap to nearest endpoint
            const p = Math.min(cur / collapseDistance, 1);
            if (p > 0 && p < 1) {
              // Content barely taller than the container: the whole scroll range is shorter than
              // collapseDistance, so the user can only ever reach p < 1. Snapping back to 0 here would
              // scroll them away from the bottom every time they stop there ("can't scroll to the end,
              // it keeps pushing back"). At the bottom, treat the header as collapsed and stay put.
              const el = containerRef.current;
              if (el && cur >= el.scrollHeight - el.clientHeight - 1) {
                applyProgress(1, true);
                return;
              }
              const snapped = p >= 0.5 ? 1 : 0;
              applyProgress(snapped, true);
              containerRef.current?.scrollTo({
                top: snapped === 1 ? collapseDistance : 0,
                behavior: 'smooth',
              });
            }
          } else {
            scrollSnapTimer.current = setTimeout(checkStopped, 16);
          }
        };

        scrollSnapTimer.current = setTimeout(checkStopped, 50);
      }
    },
    [containerRef, collapseDistance, applyProgress],
  );

  return handleScroll;
}
